/**
 * Data hook for searching buyer users when adding to a chat
 * Follows GraphQL separation pattern - handles GraphQL operations and maps to domain types
 */

import type { BuyerSelectOption } from '@choco-admin/types';
import { useCallback, useEffect, useMemo } from 'react';

import { useGetBuyerUsersLazyQuery } from '../../../generated';

export interface UseSearchBuyerUsersConfig {
  buyerId: string;
}

export interface UseSearchBuyerUsersResponse {
  users: BuyerSelectOption[];
  isLoading: boolean;
  error: Error | null;
  searchUsers: (searchTerm: string) => Promise<void>;
  clearResults: () => void;
}

export const useSearchBuyerUsers = (
  config: UseSearchBuyerUsersConfig
): UseSearchBuyerUsersResponse => {
  const { buyerId } = config;

  const [executeSearch, { data, loading, error }] = useGetBuyerUsersLazyQuery({
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  // Load initial data when buyerId is provided
  useEffect(() => {
    if (buyerId) {
      executeSearch({
        variables: {
          buyerEntityId: buyerId,
          first: 20,
        },
      });
    }
  }, [buyerId, executeSearch]);

  const searchUsers = useCallback(
    async (searchTerm: string) => {
      if (!buyerId) return;

      try {
        await executeSearch({
          variables: {
            buyerEntityId: buyerId,
            first: 20,
          },
        });
      } catch (err) {
        const usersError = err as Error;
        throw usersError;
      }
    },
    [executeSearch, buyerId]
  );

  const clearResults = useCallback(() => {
    // Clear results by executing a search with empty term
    executeSearch({
      variables: {
        buyerEntityId: buyerId,
        first: 0,
      },
    });
  }, [executeSearch, buyerId]);

  const users = useMemo(() => {
    const searchResult = data?.buyer?.users;

    if (!searchResult?.edges) {
      return [];
    }

    return searchResult.edges.map((edge) => ({
      value: edge.node.id,
      label: edge.node.name || '',
      metaData: {
        fullAddress: '',
        name: edge.node.name || '',
        referenceId: edge.node.id,
      },
    }));
  }, [data]);

  return {
    users,
    isLoading: loading,
    error: error || null,
    searchUsers,
    clearResults,
  };
};
